"use client";

import { supabase } from "@/lib/supabase";
import { normaliseAgentToolRows, type AgentToolRow } from "@/lib/agent-chat-request";
import { ilikeContainsFilter, quotePostgrestValue } from "@/lib/postgrest-filter";
import type { DbAgent, DbAgentTeam, DbAgentUsage } from "@/types/db";
import { useCallback, useEffect, useState } from "react";

type AgentTeamRow = {
  teams:
    | { id: string; name: string; is_home: boolean }
    | { id: string; name: string; is_home: boolean }[]
    | null;
};

type AgentTeamLinkRow = {
  agent_id: string;
  teams: AgentTeamRow["teams"];
};

type AgentRow = Omit<DbAgent, "teams" | "tools" | "knowledge_base_config" | "web_search_config" | "resolve_date_time_config"> & {
  agent_teams?: AgentTeamRow[] | null;
  agent_tools?: AgentToolRow[] | null;
};

type OwnerProfileRow = {
  user_id: string;
  display_name: string | null;
  username: string | null;
  email?: string | null;
};

function normaliseTeams(rows: AgentTeamRow[] | null): DbAgentTeam[] {
  return (rows ?? [])
    .map(({ teams }) =>
      Array.isArray(teams) ? (teams[0] ?? null) : teams
    )
    .filter((team): team is DbAgentTeam => Boolean(team))
    .reduce<DbAgentTeam[]>((unique, team) => {
      if (unique.some((item) => item.id === team.id)) return unique;
      unique.push(team);
      return unique;
    }, [])
    .sort((a, b) => Number(b.is_home) - Number(a.is_home) || a.name.localeCompare(b.name));
}

function normalise(rows: AgentRow[] | null): DbAgent[] {
  if (!rows) return [];

  return rows.map(({ agent_teams, agent_tools, ...agent }) => {
    const teams = normaliseTeams(agent_teams ?? null);
    const { tools: rawTools, knowledge_base_config, web_search_config, resolve_date_time_config } = normaliseAgentToolRows(agent_tools ?? null);
    // When tools_configured=true the list is authoritative (may be empty []).
    // When tools_configured=false fall back to null so user profile tools apply.
    const tools = (agent as { tools_configured?: boolean }).tools_configured
      ? rawTools
      : null;

    return { ...agent, teams, tools, knowledge_base_config, web_search_config, resolve_date_time_config } as DbAgent;
  });
}

export type UseAgentsReturn = {
  agents: DbAgent[];
  loading: boolean;
  refresh: () => void;
};

const BASE_AGENT_SELECT = `
  id,
  user_id,
  name,
  nickname,
  description,
  type,
  model,
  image_path,
  prompt,
  temperature,
  context_length,
  embeddings_provider,
  include_profile_context,
  include_team_instructions,
  include_woods_context,
  include_nz_context,
  include_civil_engineering_context,
  tools_configured,
  created_at,
  updated_at,
  reviewed_at,
  stage,
  approved_at,
  pass_threshold,
  last_test_score,
  last_tested_at,
  lifecycle_legacy
`;

const AGENT_TOOLS_SELECT = `
  agent_tools (
    config,
    tools (id, source_type)
  )
`;

async function buildOwnerMap(
  rows: Array<Pick<DbAgent, "user_id">>
): Promise<Map<string, OwnerProfileRow>> {
  const ownerIds = [...new Set(rows.map((row) => row.user_id).filter(Boolean))];
  if (ownerIds.length === 0) return new Map();

  const [{ data: ownerRows, error: ownerError }, { data: emailRows }] = await Promise.all([
    supabase.from("profiles").select("user_id, display_name, username").in("user_id", ownerIds),
    // Emails only come back for people the viewer is allowed to look up.
    supabase.from("user_search_view").select("user_id, email").in("user_id", ownerIds),
  ]);

  if (ownerError) {
    console.error("[use-agents] owner profile fetch error:", ownerError);
    return new Map();
  }

  const emailById = new Map(
    ((emailRows as Array<{ user_id: string; email: string | null }> | null) ?? []).map((row) => [row.user_id, row.email])
  );
  return new Map(
    ((ownerRows as OwnerProfileRow[] | null) ?? []).map((row) => [
      row.user_id,
      { ...row, email: emailById.get(row.user_id) ?? null },
    ])
  );
}

function applyOwnerMetadata(
  rows: DbAgent[],
  ownerById: Map<string, OwnerProfileRow>
): DbAgent[] {
  return rows.map((agent) => {
    const owner = ownerById.get(agent.user_id);
    return {
      ...agent,
      owner_display_name: owner?.display_name ?? null,
      owner_username: owner?.username ?? null,
      owner_email: owner?.email ?? null,
    };
  });
}

// ── Cursor pagination ──────────────────────────────────────────────────────────
// Keyset cursor for the (name ASC, id ASC) ordering — matches idx_agents_name_id
// so each page is a single index scan. Names are free text, so the cursor
// value must be quoted before embedding in the `.or()` filter.

export type AgentsCursor = { name: string; id: string };

export function agentCursorFromRow(agent: DbAgent): AgentsCursor {
  return { name: agent.name, id: agent.id };
}

async function assembleFallbackAgents(
  agentsList: Omit<AgentRow, "agent_teams" | "agent_tools">[]
): Promise<DbAgent[]> {
  const agentIds = agentsList.map((a) => a.id);
  const teamRowsByAgentId = new Map<string, AgentTeamRow[]>();
  const toolRowsByAgentId = new Map<string, AgentToolRow[]>();

  if (agentIds.length > 0) {
    const [teamLinks, toolLinks] = await Promise.all([
      supabase
        .from("agent_teams")
        .select("agent_id, teams (id, name, is_home)")
        .in("agent_id", agentIds),
      supabase
        .from("agent_tools")
        .select("agent_id, config, tools (id, source_type)")
        .in("agent_id", agentIds),
    ]);

    for (const row of (teamLinks.data as AgentTeamLinkRow[] | null) ?? []) {
      const existing = teamRowsByAgentId.get(row.agent_id) ?? [];
      existing.push({ teams: row.teams });
      teamRowsByAgentId.set(row.agent_id, existing);
    }

    type ToolLinkRow = AgentToolRow & { agent_id: string };
    for (const row of (toolLinks.data as ToolLinkRow[] | null) ?? []) {
      const existing = toolRowsByAgentId.get(row.agent_id) ?? [];
      existing.push({ config: row.config, tools: row.tools });
      toolRowsByAgentId.set(row.agent_id, existing);
    }
  }

  return agentsList.map((agent) => {
    const teams = normaliseTeams(teamRowsByAgentId.get(agent.id) ?? null);
    const { tools: rawTools, knowledge_base_config, web_search_config, resolve_date_time_config } = normaliseAgentToolRows(
      toolRowsByAgentId.get(agent.id) ?? null
    );
    const toolsConfigured = (agent as { tools_configured?: boolean }).tools_configured ?? false;
    const tools = toolsConfigured ? rawTools : null;
    return { ...agent, teams, tools, knowledge_base_config, web_search_config, resolve_date_time_config } as DbAgent;
  });
}

/**
 * Attaches precomputed usage (agent_usage_stats, refreshed every 10 minutes by
 * pg_cron) — one indexed lookup per page, never a count over chats.
 */
async function withUsageStats(rows: DbAgent[]): Promise<DbAgent[]> {
  if (rows.length === 0) return rows;
  const { data, error } = await supabase
    .from("agent_usage_stats")
    .select("agent_id, uses_30d, unique_users_30d, last_used_at")
    .in("agent_id", rows.map((row) => row.id));
  if (error) {
    // Usage is decoration: a failed lookup leaves it blank rather than failing the list.
    console.error("[fetchAgentsPage] usage stats fetch error:", error);
    return rows;
  }
  const byId = new Map(
    ((data as Array<{ agent_id: string } & DbAgentUsage> | null) ?? []).map(({ agent_id, ...usage }) => [agent_id, usage])
  );
  return rows.map((row) => ({ ...row, usage: byId.get(row.id) ?? null }));
}

export async function fetchAgentsPage(
  cursor: AgentsCursor | null,
  pageSize: number,
  search?: string
): Promise<DbAgent[]> {
  const trimmedSearch = search?.trim();
  const searchOr = trimmedSearch
    ? [ilikeContainsFilter("name", trimmedSearch), ilikeContainsFilter("description", trimmedSearch)].join(",")
    : null;

  let query = supabase
    .from("agents")
    .select(`
      ${BASE_AGENT_SELECT},
      agent_teams (
        teams (id, name, is_home)
      ),
      ${AGENT_TOOLS_SELECT}
    `)
    .order("name", { ascending: true })
    .order("id", { ascending: true })
    .limit(pageSize);

  if (searchOr) {
    query = query.or(searchOr);
  }

  if (cursor) {
    const name = quotePostgrestValue(cursor.name);
    query = query.or(`name.gt.${name},and(name.eq.${name},id.gt.${cursor.id})`);
  }

  const { data, error } = await query;

  if (error?.code === "PGRST201") {
    let baseQuery = supabase
      .from("agents")
      .select(BASE_AGENT_SELECT)
      .order("name", { ascending: true })
      .order("id", { ascending: true })
      .limit(pageSize);

    if (searchOr) {
      baseQuery = baseQuery.or(searchOr);
    }

    if (cursor) {
      const name = quotePostgrestValue(cursor.name);
      baseQuery = baseQuery.or(`name.gt.${name},and(name.eq.${name},id.gt.${cursor.id})`);
    }

    const { data: baseAgents, error: baseError } = await baseQuery;
    if (baseError) {
      console.error("[fetchAgentsPage] fallback agents fetch error:", baseError);
      return [];
    }

    const agentsList = (baseAgents as Omit<AgentRow, "agent_teams" | "agent_tools">[] | null) ?? [];
    const assembled = await assembleFallbackAgents(agentsList);
    return withUsageStats(applyOwnerMetadata(assembled, await buildOwnerMap(assembled)));
  }

  if (error) {
    console.error("[fetchAgentsPage] fetch error:", error);
    return [];
  }

  const normalized = normalise((data as AgentRow[] | null) ?? null);
  return withUsageStats(applyOwnerMetadata(normalized, await buildOwnerMap(normalized)));
}

export function useAgents(): UseAgentsReturn {
  const [agents, setAgents] = useState<DbAgent[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAgents = useCallback(async () => {
    setLoading(true);

    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;

    if (!user) {
      setAgents([]);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from("agents")
      .select(`
        ${BASE_AGENT_SELECT},
        agent_teams (
          teams (id, name, is_home)
        ),
        ${AGENT_TOOLS_SELECT}
      `)
      .order("name", { ascending: true });

    if (error?.code === "PGRST201") {
      // Fallback: fetch without joins, then load teams + tools separately
      const { data: baseAgents, error: baseError } = await supabase
        .from("agents")
        .select(BASE_AGENT_SELECT)
        .order("name", { ascending: true });

      if (baseError) {
        console.error("[useAgents] fallback agents fetch error:", baseError);
        setAgents([]);
        setLoading(false);
        return;
      }

      const agentsList = (baseAgents as Omit<AgentRow, "agent_teams" | "agent_tools">[] | null) ?? [];
      const assembled = await assembleFallbackAgents(agentsList);
      setAgents(applyOwnerMetadata(assembled, await buildOwnerMap(assembled)));
      setLoading(false);
      return;
    }

    if (error) {
      console.error("[useAgents] fetch error:", error);
    }

    const normalized = normalise((data as AgentRow[] | null) ?? null);
    setAgents(applyOwnerMetadata(normalized, await buildOwnerMap(normalized)));
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;

    queueMicrotask(() => {
      if (!cancelled) void fetchAgents();
    });

    return () => { cancelled = true; };
  }, [fetchAgents]);

  return { agents, loading, refresh: fetchAgents };
}
